/**
 * Conversations whose agent runtime is compacting its context right now.
 *
 * Fed by the `runtime.compaction` browser event, which every window applies
 * through the frame reducer, so this store is not mirrored. Each start arms a
 * deadline (the runtime's own timeout plus a grace period): a missing
 * "finished" event can never leave the note up for good.
 */
import { create } from "zustand";

/** Grace beyond the runtime's own timeout before the note clears itself. */
const COMPACTION_GRACE_MS = 15_000;

interface ConversationCompactionState {
  /** Conversation id → epoch ms when the note clears itself. */
  compacting: Record<string, number>;
  start: (conversationId: string, timeoutSeconds: number) => void;
  finish: (conversationId: string) => void;
}

const expiryTimers = new Map<string, ReturnType<typeof setTimeout>>();

function clearExpiry(conversationId: string): void {
  const timer = expiryTimers.get(conversationId);
  if (timer === undefined) return;
  clearTimeout(timer);
  expiryTimers.delete(conversationId);
}

export const useConversationCompactionStore =
  create<ConversationCompactionState>()((set, get) => ({
    compacting: {},

    start: (conversationId, timeoutSeconds) => {
      clearExpiry(conversationId);
      const delayMs = Math.max(0, timeoutSeconds) * 1_000 + COMPACTION_GRACE_MS;
      expiryTimers.set(
        conversationId,
        setTimeout(() => {
          expiryTimers.delete(conversationId);
          get().finish(conversationId);
        }, delayMs),
      );
      set((state) => ({
        compacting: {
          ...state.compacting,
          [conversationId]: Date.now() + delayMs,
        },
      }));
    },

    finish: (conversationId) => {
      clearExpiry(conversationId);
      set((state) => {
        if (!(conversationId in state.compacting)) return state;
        const compacting = { ...state.compacting };
        delete compacting[conversationId];
        return { compacting };
      });
    },
  }));

/** Whether the conversation's runtime is compacting its context. */
export function useConversationCompacting(
  conversationId: string | undefined,
): boolean {
  return useConversationCompactionStore((state) =>
    conversationId ? conversationId in state.compacting : false,
  );
}
